import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  InputText,
  Select,
  SelectListPosition,
  IconTrash,
  ButtonPseudo,
  ButtonSize,
} from '@ui/src';
import { CustomerStatus } from '../../../../hoc/common/types';
import { FeeAmountTypeEnum } from '../../../../hoc/common/tariff/types';
import { FeeAttributesTypes } from '../types';

import cn from '../styles.module.css';

const FeeAttributes = ({
  control,
  errors,
  isEditable,
  baseCurrency,
  currencies,
  feeAmountType,
  regularFee,
  feeCurrencyId,
  feeCascadeCurrenciesIds,
  setValue,
  status,
  currenciesList,
}: FeeAttributesTypes) => {
  const [cascadeCurrencies, setCascadeCurrencies] = useState<string[]>(['']);

  useEffect(() => {
    if (feeCascadeCurrenciesIds && feeCascadeCurrenciesIds.length > 0) {
      setCascadeCurrencies(feeCascadeCurrenciesIds);
    }
  }, [feeCascadeCurrenciesIds]);

  const currenciesWithoutBaseCurrency = currencies
    ?.filter((item) => item.value !== baseCurrency?.id)
    .sort((a, b) => a?.name?.localeCompare(b?.name));

  currenciesWithoutBaseCurrency?.unshift({
    name: baseCurrency?.description as string,
    value: baseCurrency?.id as string,
    suffix: baseCurrency?.name,
  });

  let content;

  if (feeAmountType === FeeAmountTypeEnum.PERCENT) {
    content = (
      <Controller
        control={control}
        name="feePercentAmount"
        render={({ field: { onChange, value } }) => (
          <InputText
            name="feePercentAmount"
            placeholder="Enter Write-off value"
            title="Write-off value"
            onChange={onChange}
            value={value}
            error={errors?.feePercentAmount?.message}
            disabled={!isEditable || status === CustomerStatus.CLOSED}
            className={`${cn.percentInput} ${cn.writeOffInput}`}
            suffix="%"
          />
        )}
      />
    );
  } else {
    content = (
      <Controller
        control={control}
        name="feeFixedAmount"
        render={({ field: { onChange, value } }) => (
          <InputText
            name="feeFixedAmount"
            placeholder="Enter Write-off value"
            title="Write-off value"
            suffix={
              feeCurrencyId
                ? currencies?.find((item) => item.value === feeCurrencyId)
                    ?.suffix
                : baseCurrency?.name
            }
            onChange={onChange}
            value={value}
            error={errors?.feeFixedAmount?.message}
            className={cn.writeOffInput}
            disabled={!isEditable || status === CustomerStatus.CLOSED}
            currency={
              feeCurrencyId
                ? currenciesList?.find((item) => item.id === feeCurrencyId)
                : baseCurrency
            }
            amountInput
          />
        )}
      />
    );
  }

  return (
    <>
      <div className={cn.feeAttributeWrapper}>
        <div className={cn.feeAttributeItem}>
          <Controller
            control={control}
            name="feeAmountType"
            defaultValue={undefined}
            render={({ field: { onChange, value } }) => (
              <>
                <Select
                  name="writeOffType"
                  title="Write-off type"
                  list={
                    regularFee
                      ? [
                          {
                            name: 'Fixed value',
                            value: FeeAmountTypeEnum.FIXED,
                          },
                        ]
                      : [
                          {
                            name: 'Percent of balance',
                            value: FeeAmountTypeEnum.PERCENT,
                          },
                          {
                            name: 'Fixed value',
                            value: FeeAmountTypeEnum.FIXED,
                          },
                        ]
                  }
                  onChange={onChange}
                  placeholder="Select write-off type"
                  value={value}
                  selectWrapperClassName={`${cn.feeFormSelect} ${
                    errors?.feeAmountType?.message ? cn.feeFormSelectError : ''
                  }`}
                  disabled={!isEditable || status === CustomerStatus.CLOSED}
                />
                {errors?.feeAmountType?.message && (
                  <div className={cn.feeFormSelectErrorText}>
                    {errors?.feeAmountType?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>
        <div className={cn.feeAttributeItem}>{content}</div>
      </div>
      {regularFee && (
        <>
          <Controller
            control={control}
            name="feeCurrencyId"
            render={({ field: { onChange, value, name } }) => (
              <Select
                name={name}
                list={currenciesWithoutBaseCurrency || []}
                title="Write-off currency"
                placeholder="Select write-off currency"
                onChange={onChange}
                value={value}
                error={errors?.feeCurrencyId?.message}
                disabled={!isEditable || status === CustomerStatus.CLOSED}
                selectListPosition={SelectListPosition.TOP}
                fullWidth
              />
            )}
          />
          <div className={cn.cascadeCurrenciesTitle}>
            Prioritization of currency selections
          </div>
          {cascadeCurrencies.map((item, index) => (
            <div className={cn.cascadeCurrency} key={item}>
              <Select
                name={`cascade-currency-${item}`}
                value={item}
                className={cn.cascadeCurrencySelect}
                list={
                  currenciesWithoutBaseCurrency?.map((currency) => ({
                    name: currency.name,
                    value: currency.value,
                    suffix: currency.suffix,
                    hidden: Boolean(
                      cascadeCurrencies?.find(
                        (cascadeCurrencyId) =>
                          cascadeCurrencyId === currency.value,
                      ),
                    ),
                  })) || []
                }
                disabled={status === CustomerStatus.CLOSED}
                selectListPosition={SelectListPosition.TOP}
                onChange={(e) => {
                  let nextCurrencies = ([] as string[]).concat(
                    cascadeCurrencies,
                  );

                  if (cascadeCurrencies.length === 1) {
                    nextCurrencies = [e.target.value as string];
                  } else {
                    nextCurrencies[index] = e.target.value as string;
                  }

                  setCascadeCurrencies(nextCurrencies);
                  setValue('feeCascadeCurrenciesIds', nextCurrencies);
                }}
                title="Currency"
                placeholder="Select currency"
                fullWidth
              />
              {status !== CustomerStatus.CLOSED && (
                <button
                  type="button"
                  className={cn.deleteCascadeCurrencyButton}
                  onClick={() => {
                    const nextCurrencies = cascadeCurrencies.filter(
                      (currency) => currency !== item,
                    );
                    setCascadeCurrencies(nextCurrencies);
                    setValue('feeCascadeCurrenciesIds', nextCurrencies);
                  }}
                  aria-label="Delete currency button"
                >
                  <IconTrash />
                </button>
              )}
            </div>
          ))}
          {status !== CustomerStatus.CLOSED && (
            <ButtonPseudo
              text="+ Add currency"
              className={cn.addCurrencyButton}
              size={ButtonSize.SMALL}
              disabled={Boolean(
                cascadeCurrencies.filter((item) => item === '').length,
              )}
              onClick={() => {
                const nextCurrencies = ([] as string[]).concat(
                  cascadeCurrencies,
                );

                nextCurrencies.push('');

                setCascadeCurrencies(nextCurrencies);
              }}
            />
          )}
          <Controller
            control={control}
            name="feeCascadeCurrenciesIds"
            render={({ field: { value, name } }) => (
              <input type="hidden" value={value} name={name} />
            )}
          />
        </>
      )}
      <div className={cn.feeAttributeWrapper}>
        {feeAmountType === FeeAmountTypeEnum.PERCENT && (
          <>
            <div className={cn.feeAttributeItem}>
              <Controller
                control={control}
                name="feePercentMinAmount"
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="feePercentMinAmount"
                    placeholder="Enter Minimum amount allowed"
                    title="Minimum amount allowed"
                    onChange={onChange}
                    value={value}
                    error={errors?.feePercentMinAmount?.message}
                    disabled={!isEditable || status === CustomerStatus.CLOSED}
                    suffix={baseCurrency?.name}
                    className={cn.writeOffInput}
                    currency={baseCurrency}
                    amountInput
                  />
                )}
              />
            </div>
            <div className={cn.feeAttributeItem}>
              <Controller
                control={control}
                name="feePercentMaxAmount"
                render={({ field: { onChange, value } }) => (
                  <InputText
                    name="feePercentMaxAmount"
                    placeholder="Enter Maximum amount allowed"
                    title="Maximum amount allowed"
                    onChange={onChange}
                    value={value}
                    error={errors?.feePercentMaxAmount?.message}
                    disabled={!isEditable || status === CustomerStatus.CLOSED}
                    suffix={baseCurrency?.name}
                    className={cn.writeOffInput}
                    currency={baseCurrency}
                    amountInput
                  />
                )}
              />
            </div>
          </>
        )}
      </div>
      {!regularFee && (
        <Select
          name="currencyId"
          title="Currency"
          className={cn.currencySelect}
          value={baseCurrency?.id || ''}
          list={currencies || []}
          disabled
          fullWidth
        />
      )}

      <Controller
        control={control}
        name="feeTargetAccountNumber"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="feeTargetAccountNumber"
            placeholder="Enter Fee Account"
            title="Fee Account No."
            onChange={onChange}
            value={value}
            error={errors?.feeTargetAccountNumber?.message}
            disabled
          />
        )}
      />
    </>
  );
};

export default FeeAttributes;
